import { useState, useEffect } from "react";
import {
  apiGetProducts, apiAddProduct, apiUpdateProduct, apiDeleteProduct,
  apiLogin, apiGetCategories, apiGetOrders, apiGetUsers, apiUpdateOrderStatus,
  apiApproveRejectPayment, apiUploadImage
} from "../services/api";
import {
  FiGrid, FiShoppingBag, FiUsers, FiLogOut, FiPlus, FiEdit2,
  FiTrash2, FiSearch, FiX, FiCheck, FiPackage, FiChevronDown
} from "react-icons/fi";
import "./Admin.css";

const BADGES  = ["", "New", "Hot", "Sale"];
const EMPTY   = { name:"", price:"", original_price:"", category:"", description:"", badge:"", rating:"4.5", reviews:"0", image:"", sizes:"", colors:"" };
const STATUS_COLORS = { pending:"#f59e0b", confirmed:"#3b82f6", shipped:"#8b5cf6", delivered:"#10b981", cancelled:"#ef4444" };
const PAY_COLORS   = { pending_verification:"#f59e0b", approved:"#10b981", rejected:"#ef4444" };

export default function Admin() {
  const [authed, setAuthed]   = useState(() => !!localStorage.getItem("rizzglam_token"));
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [pwError, setPwError] = useState("");
  const [page, setPage]       = useState("products"); // products | orders | users
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders]   = useState([]);
  const [users, setUsers]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch]   = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [toast, setToast]     = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const res = await apiUploadImage(file);
    setUploading(false);
    if (res.url) {
      setForm(f => ({ ...f, image: res.url }));
      setImgPreview(res.url);
    } else {
      showToast("❌ Upload failed: " + (res.error || "Unknown error"));
    }
  };
  const [loading, setLoading] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2800); };

  useEffect(() => {
    apiGetCategories().then(data => {
      const cats = Array.isArray(data) ? data.filter(c => c.slug !== "all") : [];
      setCategories(cats);
      if (cats.length) setForm(f => ({ ...f, category: cats[0].slug }));
    });
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadProducts();
    apiGetOrders().then(d => setOrders(Array.isArray(d) ? d : []));
    apiGetUsers().then(d => setUsers(Array.isArray(d) ? d : []));
  }, [authed]);

  const loadProducts = async () => {
    const data = await apiGetProducts();
    setProducts(Array.isArray(data) ? data : []);
  };

  const login = async () => {
    setPwError("");
    const res = await apiLogin({ email, password });
    if (res.error) { setPwError(res.error); return; }
    if (res.user?.role !== "admin") { setPwError("Access denied. Admins only."); return; }
    localStorage.setItem("rizzglam_token", res.token);
    setAuthed(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (name === "image") setImgPreview(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.image) return showToast("⚠️ Name, Price & Image required.");
    setLoading(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      rating: parseFloat(form.rating) || 4.5,
      reviews: parseInt(form.reviews) || 0,
      sizes:  form.sizes.split(",").map(s => s.trim()).filter(Boolean),
      colors: form.colors.split(",").map(c => c.trim()).filter(Boolean),
      badge: form.badge || null,
    };
    const res = editId !== null
      ? await apiUpdateProduct(editId, payload)
      : await apiAddProduct(payload);
    if (res.error) { showToast("❌ " + res.error); setLoading(false); return; }
    showToast(editId ? "✅ Product updated!" : "✅ Product added!");
    await loadProducts();
    setForm(EMPTY); setEditId(null); setImgPreview(""); setShowForm(false); setLoading(false);
  };

  const startEdit = (p) => {
    setForm({
      name: p.name, price: p.price, original_price: p.original_price || "",
      category: p.category, description: p.description || "",
      badge: p.badge || "", rating: p.rating, reviews: p.reviews, image: p.image,
      sizes:  Array.isArray(p.sizes)  ? p.sizes.join(", ")  : p.sizes  || "",
      colors: Array.isArray(p.colors) ? p.colors.join(", ") : p.colors || "",
    });
    setEditId(p.id); setImgPreview(p.image); setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => { setForm(EMPTY); setEditId(null); setImgPreview(""); setShowForm(false); };

  const confirmDelete = async () => {
    const res = await apiDeleteProduct(deleteConfirm);
    if (res.error) showToast("❌ " + res.error);
    else { showToast("🗑️ Deleted."); await loadProducts(); }
    setDeleteConfirm(null);
  };

  const handlePaymentAction = async (orderId, action, note) => {
    await apiApproveRejectPayment(orderId, action, note);
    setOrders(prev => prev.map(o => o.id === orderId
      ? { ...o, payment_status: action, status: action === "approved" ? "confirmed" : "cancelled", admin_note: note }
      : o
    ));
    showToast(action === "approved" ? "✅ Payment Approved!" : "❌ Payment Rejected.");
  };

  const handleStatusChange = async (orderId, status) => {
    await apiUpdateOrderStatus(orderId, status);
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    showToast("✅ Order status updated.");
  };

  // Group products by category
  const filteredProducts = products.filter(p => {
    const matchCat    = filterCat === "all" || p.category === filterCat;
    const matchSearch = String(p.name || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const grouped = categories.reduce((acc, cat) => {
    const items = filteredProducts.filter(p => p.category === cat.slug);
    if (items.length) acc[cat.slug] = { name: cat.name, items };
    return acc;
  }, {});
  // uncategorized
  const knownSlugs = categories.map(c => c.slug);
  const uncategorized = filteredProducts.filter(p => !knownSlugs.includes(p.category));
  if (uncategorized.length) grouped["other"] = { name: "Other", items: uncategorized };

  // Stats
  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total || 0), 0);

  // ── Login ──
  if (!authed) return (
    <div className="admin-login">
      <div className="login-box">
        <div className="login-logo"><span className="lm">Rizz</span><span className="la">Glam</span></div>
        <h2>Admin Panel</h2>
        <p>Sign in with your admin credentials</p>
        <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input type="password" placeholder="Password" value={password}
          onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && login()}
          className={pwError ? "shake" : ""} />
        {pwError && <span className="login-error">{pwError}</span>}
        <button onClick={login}>Login</button>
      </div>
    </div>
  );

  return (
    <div className="admin-page">
      {toast && <div className="admin-toast">{toast}</div>}

      {/* ── Sidebar ── */}
      <aside className="admin-sidebar">
        <div className="sidebar-logo"><span className="lm">Rizz</span><span className="la">Glam</span></div>
        <nav className="sidebar-nav">
          <button className={page === "products" ? "active" : ""} onClick={() => setPage("products")}><FiShoppingBag /> Products</button>
          <button className={page === "orders"   ? "active" : ""} onClick={() => setPage("orders")}><FiPackage /> Orders <span className="nav-badge">{orders.length}</span></button>
          <button className={page === "users"    ? "active" : ""} onClick={() => setPage("users")}><FiUsers /> Customers <span className="nav-badge">{users.length}</span></button>
        </nav>
        <button className="sidebar-logout" onClick={() => { localStorage.removeItem("rizzglam_token"); setAuthed(false); }}><FiLogOut /> Logout</button>
      </aside>

      {/* ── Main ── */}
      <div className="admin-main">

        {/* ── Top bar ── */}
        <div className="admin-topbar">
          <div className="topbar-title">
            {page === "products" && <><FiShoppingBag /> Products</>}
            {page === "orders"   && <><FiPackage /> Orders</>}
            {page === "users"    && <><FiUsers /> Customers</>}
          </div>
          {page === "products" && (
            <button className="btn-add-product" onClick={() => { cancelForm(); setShowForm(true); }}>
              <FiPlus /> Add Product
            </button>
          )}
        </div>

        {/* ── Stats bar ── */}
        <div className="stats-bar">
          <div className="stat-card"><span className="stat-val">{products.length}</span><span className="stat-label">Products</span></div>
          <div className="stat-card"><span className="stat-val">{orders.length}</span><span className="stat-label">Orders</span></div>
          <div className="stat-card"><span className="stat-val">{users.filter(u => u.role !== "admin").length}</span><span className="stat-label">Customers</span></div>
          <div className="stat-card"><span className="stat-val">Rs. {totalRevenue.toLocaleString()}</span><span className="stat-label">Revenue</span></div>
        </div>

        {/* ════════════ PRODUCTS PAGE ════════════ */}
        {page === "products" && (
          <div className="page-content">

            {/* Add/Edit Form */}
            {showForm && (
              <div className="form-card">
                <div className="form-card-header">
                  <h2>{editId ? "✏️ Edit Product" : "➕ Add New Product"}</h2>
                  <button className="btn-icon" onClick={cancelForm}><FiX /></button>
                </div>
                <form onSubmit={handleSubmit} className="admin-form">
                  <div className="form-row">
                    <div className="form-group full">
                      <label>Product Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Embroidered Lawn 3-Piece" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Price (Rs.) *</label>
                      <input name="price" type="number" value={form.price} onChange={handleChange} placeholder="3499" />
                    </div>
                    <div className="form-group">
                      <label>Original Price (Rs.)</label>
                      <input name="original_price" type="number" value={form.original_price} onChange={handleChange} placeholder="4999" />
                    </div>
                    <div className="form-group">
                      <label>Badge</label>
                      <select name="badge" value={form.badge} onChange={handleChange}>
                        {BADGES.map(b => <option key={b} value={b}>{b || "None"}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Category *</label>
                      <select name="category" value={form.category} onChange={handleChange}>
                        {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Rating (0–5)</label>
                      <input name="rating" type="number" step="0.1" min="0" max="5" value={form.rating} onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label>Reviews Count</label>
                      <input name="reviews" type="number" value={form.reviews} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Sizes (comma separated)</label>
                      <input name="sizes" value={form.sizes} onChange={handleChange} placeholder="S, M, L, XL" />
                    </div>
                    <div className="form-group">
                      <label>Colors (comma separated)</label>
                      <input name="colors" value={form.colors} onChange={handleChange} placeholder="Red, Blue, Green" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group full">
                      <label>Image *</label>
                      <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ flex:1 }}
                        />
                        {uploading && <span style={{ fontSize:"13px", color:"#888" }}>Uploading...</span>}
                      </div>
                      <input
                        name="image"
                        value={form.image}
                        onChange={handleChange}
                        placeholder="Or paste image URL"
                        style={{ marginTop:"6px" }}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group full">
                      <label>Description</label>
                      <textarea name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Product description..." />
                    </div>
                  </div>
                  {imgPreview && (
                    <div className="img-preview">
                      <img src={imgPreview} alt="preview" onError={() => setImgPreview("")} />
                      <span>Image Preview</span>
                    </div>
                  )}
                  <div className="form-actions">
                    <button type="submit" className="btn-submit" disabled={loading}>{loading ? "Saving..." : editId ? "Update Product" : "Add Product"}</button>
                    <button type="button" className="btn-cancel" onClick={cancelForm}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Filters */}
            <div className="products-toolbar">
              <div className="search-box">
                <FiSearch />
                <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
                {search && <button onClick={() => setSearch("")}><FiX /></button>}
              </div>
              <div className="cat-tabs">
                <button className={filterCat === "all" ? "active" : ""} onClick={() => setFilterCat("all")}>All <span>{products.length}</span></button>
                {categories.map(c => {
                  const cnt = products.filter(p => p.category === c.slug).length;
                  return cnt > 0 && (
                    <button key={c.slug} className={filterCat === c.slug ? "active" : ""} onClick={() => setFilterCat(c.slug)}>
                      {c.name} <span>{cnt}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category-wise product cards */}
            {Object.keys(grouped).length === 0
              ? <div className="empty-state"><FiShoppingBag size={48} /><p>No products found.</p></div>
              : Object.entries(grouped).map(([slug, group]) => (
                <div key={slug} className="category-section">
                  <div className="category-section-header">
                    <h3>{group.name}</h3>
                    <span className="cat-count">{group.items.length} product{group.items.length !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="products-card-grid">
                    {group.items.map(p => (
                      <div key={p.id} className="product-admin-card">
                        <div className="pac-img">
                          <img src={p.image} alt={p.name} onError={e => e.target.src = "https://via.placeholder.com/200x200?text=No+Image"} />
                          {p.badge && <span className={`pac-badge badge-${p.badge.toLowerCase()}`}>{p.badge}</span>}
                        </div>
                        <div className="pac-body">
                          <h4>{p.name}</h4>
                          <p className="pac-desc">{p.description?.slice(0, 55)}{p.description?.length > 55 ? "…" : ""}</p>
                          <div className="pac-price">
                            <strong>Rs. {Number(p.price).toLocaleString()}</strong>
                            {p.original_price && <s>Rs. {Number(p.original_price).toLocaleString()}</s>}
                          </div>
                          <div className="pac-tags">
                            {(Array.isArray(p.sizes) ? p.sizes : []).slice(0, 4).map(s => <span key={s} className="tag">{s}</span>)}
                          </div>
                          <div className="pac-rating">{"★".repeat(Math.round(p.rating || 0))}{"☆".repeat(5 - Math.round(p.rating || 0))} <span>({p.reviews || 0})</span></div>
                        </div>
                        <div className="pac-actions">
                          <button className="btn-edit-sm" onClick={() => startEdit(p)}><FiEdit2 /> Edit</button>
                          <button className="btn-del-sm"  onClick={() => setDeleteConfirm(p.id)}><FiTrash2 /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {/* ════════════ ORDERS PAGE ════════════ */}
        {page === "orders" && (
          <div className="page-content">
            {orders.length === 0
              ? <div className="empty-state"><FiPackage size={48} /><p>No orders yet.</p></div>
              : <div className="orders-grid">
                  {orders.map(o => (
                    <div key={o.id} className="order-card">
                      <div className="order-card-header">
                        <div>
                          <span className="order-id">Order #{o.id}</span>
                          <span className="order-time">{new Date(o.created_at || Date.now()).toLocaleString("en-IN", { dateStyle:"medium", timeStyle:"short", timeZone:"Asia/Kolkata" })}</span>
                        </div>
                        <select
                          className="status-select"
                          value={o.status}
                          style={{ borderColor: STATUS_COLORS[o.status] }}
                          onChange={e => handleStatusChange(o.id, e.target.value)}
                        >
                          {["pending","confirmed","shipped","delivered","cancelled"].map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                      </div>

                      <div className="order-customer">
                        <div className="cust-avatar">{(o.customer_name || o.user_name || "?")[0]?.toUpperCase()}</div>
                        <div>
                          <strong>{o.customer_name || o.user_name}</strong>
                          <span>{o.customer_email || o.user_email}</span>
                          {o.customer_phone && <span>📞 {o.customer_phone}</span>}
                          {o.address_line   && <span>🏠 {o.address_line}</span>}
                          {(o.city || o.province) && <span>📍 {[o.city, o.province, o.postal_code].filter(Boolean).join(", ")}</span>}
                          {o.upi_app        && <span>💳 {o.payment_method} via {o.upi_app}{o.payment_ref ? ` · Ref: ${o.payment_ref}` : ""}</span>}
                        </div>
                      </div>

                      <div className="order-items">
                        {(Array.isArray(o.items) ? o.items : []).map((item, i) => (
                          <div key={i} className="order-item-row">
                            <img src={item.image} alt={item.name} onError={e => e.target.src="https://via.placeholder.com/40"} />
                            <div className="order-item-info">
                              <span>{item.name}</span>
                              <span className="item-meta">
                                {item.size && `Size: ${item.size}`}
                                {item.color && ` · ${item.color}`}
                                {` · Qty: ${item.qty}`}
                              </span>
                            </div>
                            <span className="item-price">Rs. {((item.price || 0) * (item.qty || 1)).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      <div className="order-footer">
                        <span className={`status-pill status-${o.status}`}>{o.status}</span>
                        <strong className="order-total">Total: Rs. {Number(o.total || 0).toLocaleString()}</strong>
                      </div>

                      {/* Payment verification */}
                      <div className="payment-verify-row">
                        <div className="pay-status-info">
                          <span className="pay-label">Payment:</span>
                          <span className="pay-badge" style={{ background: PAY_COLORS[o.payment_status] || "#e0e0e0" }}>
                            {o.payment_status === "pending_verification" ? "⏳ Pending Verification"
                              : o.payment_status === "approved" ? "✅ Approved"
                              : "❌ Rejected"}
                          </span>
                          {o.payment_ref && <span className="utr-tag">UTR: {o.payment_ref}</span>}
                        </div>
                        {o.payment_status === "pending_verification" && (
                          <div className="pay-actions">
                            <button className="btn-approve" onClick={() => handlePaymentAction(o.id, "approved", null)}>✅ Approve</button>
                            <button className="btn-reject"  onClick={() => {
                              const note = window.prompt("Reason for rejection (optional):");
                              handlePaymentAction(o.id, "rejected", note);
                            }}>❌ Reject</button>
                          </div>
                        )}
                        {o.admin_note && <p className="admin-note">📝 {o.admin_note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        )}

        {/* ════════════ USERS PAGE ════════════ */}
        {page === "users" && (
          <div className="page-content">
            {users.filter(u => u.role !== "admin").length === 0
              ? <div className="empty-state"><FiUsers size={48} /><p>No customers yet.</p></div>
              : <div className="users-grid">
                  {users.filter(u => u.role !== "admin").map(u => {
                    const userOrders = orders.filter(o => o.user_id === u.id);
                    const spent = userOrders.reduce((s, o) => s + parseFloat(o.total || 0), 0);
                    return (
                      <div key={u.id} className="user-card">
                        <div className="user-card-top">
                          <div className="user-avatar">{u.name?.[0]?.toUpperCase()}</div>
                          <div className="user-info">
                            <strong>{u.name}</strong>
                            <span>{u.email}</span>
                            {u.phone && <span>📞 {u.phone}</span>}
                            {u.city  && <span>📍 {u.city}</span>}
                          </div>
                        </div>
                        <div className="user-stats">
                          <div className="user-stat"><span>{userOrders.length}</span><label>Orders</label></div>
                          <div className="user-stat"><span>Rs. {spent.toLocaleString()}</span><label>Spent</label></div>
                          <div className="user-stat"><span>{new Date(u.created_at || Date.now()).toLocaleDateString("en-PK")}</span><label>Joined</label></div>
                        </div>
                        {userOrders.length > 0 && (
                          <div className="user-recent-orders">
                            <p className="recent-label">Recent Orders</p>
                            {userOrders.slice(0, 2).map(o => (
                              <div key={o.id} className="user-order-row">
                                <span>#{o.id} · {new Date(o.created_at || Date.now()).toLocaleDateString("en-PK")}</span>
                                <span className={`status-pill status-${o.status}`}>{o.status}</span>
                                <span>Rs. {Number(o.total).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Delete Product?</h3>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-delete" onClick={confirmDelete}>Yes, Delete</button>
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
